import AppRoutes from "@/constants/routes/AppRoutes";
import type { AppNavigationTarget } from "@/lib/navigation/appNavigation";
import { removeReferenceHighlightParamsFromSearchParams } from "@/partials/document-renderer/utils/referenceHighlightQuery";
import { Domain, getDomain } from "@/utils/domain";
import {
  fromFolderPathSessionKey,
  pathItemsToWorkspaceFilesUrl,
} from "@/lib/navigation/documentViewerReturn";
import type { PathItem } from "@/pages/workspace/utils/folderUtils";
import {
  getDocumentKeyFromPath,
  getDocumentKeyFromTarget,
  isDocumentViewerNavigationTarget,
  isDocumentViewerPath,
} from "./documentViewerRoutes";
import {
  clearStack,
  seedStack,
  type NavigationStackFrame,
} from "./documentNavigationStack";
import { readWorkspaceSearchParams } from "./workspaceSearchParams";

export type NavigationStackMode = "push" | "replace";

let pendingNavigationStackMode: NavigationStackMode = "push";

export const setPendingNavigationStackMode = (
  mode: NavigationStackMode
): void => {
  pendingNavigationStackMode = mode;
};

export const consumePendingNavigationStackMode = (): NavigationStackMode => {
  const mode = pendingNavigationStackMode;
  pendingNavigationStackMode = "push";
  return mode;
};

export const buildCanonicalViewerReturnUrl = (
  pathname: string,
  search: string
): string => {
  const searchParams = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const cleanedSearch =
    removeReferenceHighlightParamsFromSearchParams(searchParams);
  return `${pathname}${cleanedSearch}`;
};

export const getChatRootLabel = (): string => {
  const domain =
    typeof window !== "undefined"
      ? getDomain(window.location.hostname)
      : Domain.LARA;
  return domain === Domain.LODA ? "Loda AI" : "Lara AI";
};

const extractWorkspaceIdFromPath = (pathname: string): string | null => {
  const match = /^\/workspaces\/([^/]+)/.exec(pathname);
  return match?.[1] ?? null;
};

const resolveWorkspaceRootUrl = (workspaceId: string): string => {
  const stored = sessionStorage.getItem(fromFolderPathSessionKey(workspaceId));
  if (stored) {
    try {
      const pathItems = JSON.parse(stored) as PathItem[];
      if (Array.isArray(pathItems)) {
        return pathItemsToWorkspaceFilesUrl(workspaceId, pathItems);
      }
    } catch {
      // Fall through.
    }
  }
  return AppRoutes.workspaceFiles(workspaceId);
};

/** The root of a document opened from the Workspaces search: "Tilbage" returns to that search. */
export const isWorkspaceSearchRoot = (frame?: NavigationStackFrame): boolean =>
  frame?.frameKind === "root" &&
  (frame.url === AppRoutes.workspaces ||
    frame.url.startsWith(`${AppRoutes.workspaces}?`));

const WORKSPACE_DOCUMENT_PATH = /^\/workspaces\/([^/]+)\/documents?\//;

/**
 * A workspace document opened under a trail that is not its own (a law from Kilder, a
 * document from another workspace) starts a new trail; otherwise the old root is
 * relabelled and the unrelated crumbs stay (LAR-658). Laws never start a new trail:
 * they are reached from workspace documents and chats, and the trail is the point.
 * A Workspaces-search root and a chat root keep their trail too; the viewer adds the
 * workspace as a crumb under them.
 */
export const viewerPathStartsNewTrail = (
  stack: NavigationStackFrame[],
  pathname: string
): boolean => {
  const root = stack[0];
  if (!root || root.frameKind !== "root") {
    return false;
  }
  const workspaceId = WORKSPACE_DOCUMENT_PATH.exec(pathname)?.[1];
  if (!workspaceId) {
    return false;
  }
  if (isWorkspaceSearchRoot(root)) {
    return false;
  }
  if (
    root.url === AppRoutes.chat ||
    root.url.startsWith(`${AppRoutes.chat}/`)
  ) {
    return false;
  }
  return extractWorkspaceIdFromPath(root.url) !== workspaceId;
};

export const seedRootFromPath = (pathname: string, search = ""): void => {
  // A result opened from the Workspaces search (LAR-235). The search is in the address,
  // so the root returns to exactly that search; the workspace itself becomes a crumb
  // (see useDocumentViewerStackSync). Without a search the overview lists no documents.
  if (pathname === AppRoutes.workspaces) {
    const term = readWorkspaceSearchParams(search).search;
    if (term) {
      seedStack([
        {
          label: `Søgning: “${term}”`,
          url: `${AppRoutes.workspaces}${search}`,
          frameKind: "root",
        },
      ]);
    }
    return;
  }

  if (
    pathname === AppRoutes.chat ||
    pathname.startsWith(`${AppRoutes.chat}/legislation/`)
  ) {
    seedStack([
      {
        label: getChatRootLabel(),
        url: AppRoutes.chat,
        frameKind: "root",
      },
    ]);
    return;
  }

  if (pathname.startsWith(`${AppRoutes.chat}/workspace/`)) {
    seedStack([
      {
        label: getChatRootLabel(),
        url: AppRoutes.chat,
        frameKind: "root",
      },
    ]);
    return;
  }

  const workspaceFilesMatch = /^\/workspaces\/([^/]+)\/files(?:\/|$)/.exec(
    pathname
  );
  if (workspaceFilesMatch) {
    const workspaceId = workspaceFilesMatch[1];
    seedStack([
      {
        label: "Workspace",
        url: resolveWorkspaceRootUrl(workspaceId),
        frameKind: "root",
      },
    ]);
    return;
  }

  if (
    pathname.startsWith(AppRoutes.legislations) ||
    pathname.startsWith("/docLink/")
  ) {
    seedStack([
      {
        label: "Kilder",
        url: AppRoutes.legislations,
        frameKind: "root",
      },
    ]);
    return;
  }

  const workspaceId = extractWorkspaceIdFromPath(pathname);
  if (workspaceId && pathname.includes("/workspaces/")) {
    seedStack([
      {
        label: "Workspace",
        url: resolveWorkspaceRootUrl(workspaceId),
        frameKind: "root",
      },
    ]);
  }
};

type ApplyDocumentNavigationStackOptions = {
  target: AppNavigationTarget;
  replace: boolean;
  openingNewTab: boolean;
  navigationMode?: NavigationStackMode;
  pathname?: string;
  search?: string;
};

/**
 * Prepares the navigation stack before same-tab document viewer navigation.
 */
export const applyDocumentNavigationStackOnNavigate = ({
  target,
  replace,
  openingNewTab,
  navigationMode = "push",
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
  search = typeof window !== "undefined" ? window.location.search : "",
}: ApplyDocumentNavigationStackOptions): void => {
  if (openingNewTab || replace) {
    return;
  }

  if (!isDocumentViewerNavigationTarget(target)) {
    return;
  }

  setPendingNavigationStackMode(navigationMode);

  const currentIsViewer = isDocumentViewerPath(pathname);
  if (!currentIsViewer) {
    clearStack();
    seedRootFromPath(pathname, search);
    return;
  }

  const currentKey = getDocumentKeyFromPath(pathname);
  const targetKey = getDocumentKeyFromTarget(target, pathname);
  if (!currentKey || !targetKey || currentKey === targetKey) {
    setPendingNavigationStackMode("push");
  }
};
